import { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Microphone button for speech-to-text input.
 * Click to start recording, click again to stop. Audio sent to Whisper.
 */
export const VoiceInput = ({ onTranscript, className = "", size = "md", title }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecorder = useRef(null);
  const chunks = useRef([]);

  const sizeClasses = size === "sm"
    ? { btn: "p-1.5", icon: "w-3.5 h-3.5" }
    : size === "lg"
      ? { btn: "p-4", icon: "w-6 h-6" }
      : { btn: "p-3", icon: "w-5 h-5" };

  const toggleRecording = async () => {
    if (recording) {
      // Stop recording
      if (mediaRecorder.current) {
        mediaRecorder.current.stop();
        setRecording(false);
      }
      return;
    }

    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Find supported MIME type (prefer opus, fall back through webm to mp4)
      const pickMimeType = () => {
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
        if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
        return "audio/mp4";
      };
      const mimeType = pickMimeType();

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorder.current = recorder;
      chunks.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks.current, { type: mimeType });
        if (blob.size < 500) {
          toast.error("Opname te kort. Spreek iets langer.");
          return;
        }
        setProcessing(true);
        try {
          const ext = mimeType.includes("webm") ? "webm" : "mp4";
          const formData = new FormData();
          formData.append("file", blob, `recording.${ext}`);
          const res = await axios.post(`${API}/speech-to-text`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          });
          if (res.data.text) {
            onTranscript(res.data.text);
            toast.success("Spraak herkend!");
          } else {
            toast.error("Geen spraak gedetecteerd. Probeer opnieuw.");
          }
        } catch (e) {
          const detail = e.response?.data?.detail;
          if (detail?.code === "llm_budget_exceeded") {
            toast.error(detail.message, { duration: 6000 });
          } else {
            const msg = (typeof detail === "string" ? detail : detail?.message) || "Spraakherkenning mislukt";
            toast.error(msg);
          }
        }
        setProcessing(false);
      };

      recorder.start();
      setRecording(true);
      toast.info("Opname gestart. Klik nogmaals om te stoppen.");
    } catch {
      toast.error("Microfoon toegang geweigerd. Sta microfoon toe in je browser.");
    }
  };

  if (processing) {
    return (
      <button disabled className={`${sizeClasses.btn} bg-[#2d8cf0] text-white transition-all ${className}`} data-testid="voice-processing">
        <Loader2 className={`${sizeClasses.icon} animate-spin`} />
      </button>
    );
  }

  return (
    <button
      onClick={toggleRecording}
      type="button"
      className={`${sizeClasses.btn} transition-all ${recording ? "bg-red-500 text-white animate-pulse" : "bg-[#0a0e1a] text-white hover:bg-[#2d8cf0]"} ${className}`}
      title={title || (recording ? "Klik om te stoppen" : "Klik om te spreken")}
      data-testid="voice-input-btn"
    >
      {recording ? <Square className={sizeClasses.icon} /> : <Mic className={sizeClasses.icon} />}
    </button>
  );
};
